import { useEffect, useState } from 'react';

const useServiceWorker = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Verifica suporte a Service Worker
    if (!('serviceWorker' in navigator)) {
      console.warn('⚠️ Service Worker não suportado neste navegador');
      return;
    }

    // Registra o Service Worker
    const registerServiceWorker = async () => {
      try {
        console.log('🔄 Registrando Service Worker...');
        
        const reg = await navigator.serviceWorker.register('/service-worker.js', {
          scope: '/',
          updateViaCache: 'none' // Sempre verifica atualizações
        });
        
        console.log('✅ Service Worker registrado:', reg);
        setRegistration(reg);

        // 🆕 Verifica atualizações a cada 10 MINUTOS (600000ms)
        setInterval(() => {
          console.log('🔍 Verificando atualizações do Service Worker (10min)...');
          reg.update();
        }, 600000); // 10 minutos

        // Também verifica a cada 60 segundos (backup)
        setInterval(() => {
          console.log('🔍 Verificação rápida (60s)...');
          reg.update();
        }, 60000);

        // Listener para novas versões
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          console.log('🆕 Nova versão detectada!');

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('✨ Nova versão instalada! Pronta para ativar.');
              setUpdateAvailable(true);
              
              // Auto-ativa após 3 segundos
              setTimeout(() => {
                console.log('⏩ Ativando nova versão automaticamente...');
                newWorker.postMessage({ type: 'SKIP_WAITING' });
              }, 3000);
            }
          });
        });

        // Listener para quando o SW assume controle
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          console.log('🔄 Service Worker atualizado!');
          
          // NÃO recarregar automaticamente nas páginas de login/checkout
          // para não interferir com formulários em progresso
          const currentPath = window.location.pathname;
          const noReloadPaths = ['/login', '/checkout', '/adm'];
          
          const shouldReload = !noReloadPaths.some(path => currentPath.startsWith(path));
          
          if (shouldReload) {
            console.log('🔄 Recarregando página...');
            showUpdateNotification();
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } else {
            console.log('⏸️ Atualização pendente - não recarregar (página crítica)');
            // Apenas mostrar notificação sem recarregar
            showUpdateNotification();
          }
        });

        // Listener para mensagens do SW
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data && event.data.type === 'SW_UPDATED') {
            console.log('✅ Service Worker ativado:', event.data.version);
          }
        });

      } catch (error) {
        console.error('❌ Erro ao registrar Service Worker:', error);
      }
    };

    registerServiceWorker();

    // Monitor status online/offline
    const handleOnline = () => {
      console.log('🌐 Conexão restaurada');
      setIsOnline(true);
    };

    const handleOffline = () => {
      console.log('📡 Modo offline ativado');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Função para forçar atualização manual
  const checkForUpdates = () => {
    if (registration) {
      console.log('🔍 Verificando atualizações manualmente...');
      registration.update();
    }
  };

  return {
    updateAvailable,
    isOnline,
    checkForUpdates,
    registration
  };
};

// Função helper para mostrar notificação de atualização
const showUpdateNotification = () => {
  // Cria elemento de notificação
  const notification = document.createElement('div');
  notification.style.cssText = `
    position: fixed;
    bottom: 20px;
    right: 20px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 16px 24px;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0,0,0,0.3);
    z-index: 9999;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 14px;
    font-weight: 500;
    animation: slideIn 0.3s ease-out;
    display: flex;
    align-items: center;
    gap: 12px;
  `;
  
  notification.innerHTML = `
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2Z" fill="white" fill-opacity="0.3"/>
      <path d="M13.7071 7.29289C14.0976 7.68342 14.0976 8.31658 13.7071 8.70711L9.70711 12.7071C9.31658 13.0976 8.68342 13.0976 8.29289 12.7071L6.29289 10.7071C5.90237 10.3166 5.90237 9.68342 6.29289 9.29289C6.68342 8.90237 7.31658 8.90237 7.70711 9.29289L9 10.5858L12.2929 7.29289C12.6834 6.90237 13.3166 6.90237 13.7071 7.29289Z" fill="white"/>
    </svg>
    <span>App atualizado! ✨</span>
  `;

  // Adiciona CSS de animação
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideIn {
      from {
        transform: translateX(400px);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }
    @keyframes slideOut {
      from {
        transform: translateX(0);
        opacity: 1;
      }
      to {
        transform: translateX(400px);
        opacity: 0;
      }
    }
  `;
  document.head.appendChild(style);

  document.body.appendChild(notification);

  // Remove após 3 segundos
  setTimeout(() => {
    notification.style.animation = 'slideOut 0.3s ease-out forwards';
    setTimeout(() => {
      document.body.removeChild(notification);
    }, 300);
  }, 3000);
};

export default useServiceWorker;
